/**
 * turnstile-box.tsx — Ô "Xác minh bảo mật" Cloudflare Turnstile cho form công khai.
 *
 *   const ts = useTurnstile(config?.turnstileSiteKey, 'login')        // siteKey null/undefined = chưa bật → không làm gì
 *   <TurnstileBox ts={ts} />                                           // vẽ widget (+ thông báo khi không tải được)
 *   disabled={busy || ts.pending}                                      // chờ có token mới cho gửi
 *   api.post(url, withTurnstileToken(body, ts.token)) … finally ts.reset()   // token dùng 1 lần → lấy token mới sau MỖI lần gửi
 *
 * Nạp script + kiểu: lib/turnstile.ts. Widget `flexible` (rộng theo form), theo giao diện sáng / tối của ứng dụng.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  loadTurnstile, normalizeTurnstileErrorCode, turnstileErrorDetail, turnstileFailureLog, turnstileFailureMessage, turnstileTheme,
  type TurnstileAction, type TurnstileFailure,
} from '@/lib/turnstile'

export interface TurnstileState {
  /** Đã bật (có khoá công khai)? Tắt → mọi thứ bên dưới là no-op. */
  enabled: boolean
  /** Token hiện có (null = chưa xong / đã hết hạn / đã dùng). */
  token: string | null
  /** Đã bật mà chưa có token → khoá nút gửi. */
  pending: boolean
  /** Không tải được script / widget báo lỗi (mạng, bị chặn quảng cáo, tên miền chưa khai báo ở Cloudflare). */
  failed: boolean
  /** Chi tiết lỗi (loại + mã lỗi Cloudflare) để hiện trên form; null = không lỗi. Widget tự thử lại nên có thể tự mất. */
  error: TurnstileFailure | null
  /** Xoá token + lấy token mới (gọi sau mỗi lần gửi, nhất là khi bị từ chối). Script chưa nạp được → thử nạp lại. */
  reset: () => void
  /** Gắn vào <div> chứa widget (TurnstileBox lo việc này). */
  containerRef: (el: HTMLDivElement | null) => void
}

export function useTurnstile(siteKey: string | null | undefined, action: TurnstileAction): TurnstileState {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<TurnstileFailure | null>(null)
  const [attempt, setAttempt] = useState(0)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!siteKey || !el) return
    let alive = true
    /** Ghi lỗi lên form + console (Cloudflare im lặng vì error-callback trả true) để biết đúng mã lỗi khi chẩn đoán. */
    const fail = (f: TurnstileFailure) => {
      console.warn(turnstileFailureLog(f, action))
      setToken(null)
      setError(f)
    }
    loadTurnstile().then(
      (api) => {
        if (!alive) return
        try {
          widgetId.current = api.render(el, {
            sitekey: siteKey,
            action,
            theme: turnstileTheme(),
            size: 'flexible',
            language: 'auto',
            callback: (t) => {
              setToken(t)
              setError(null)
            },
            'expired-callback': () => setToken(null),
            'timeout-callback': () => setToken(null),
            'error-callback': (code) => {
              if (alive) fail({ kind: 'widget', code: normalizeTurnstileErrorCode(code) })
              return true
            },
          })
        } catch (e) {
          // render() ném lỗi (khoá công khai sai định dạng…) — script ĐÃ tải nên không phải lỗi mạng, và widget không được dựng (không tự thử lại).
          if (alive) fail({ kind: 'render', code: null, detail: turnstileErrorDetail(e) })
        }
      },
      () => alive && fail({ kind: 'script', code: null }),
    )
    return () => {
      alive = false
      setToken(null)
      if (widgetId.current) window.turnstile?.remove(widgetId.current)
      widgetId.current = null
    }
  }, [siteKey, action, el, attempt])

  const reset = useCallback(() => {
    setToken(null)
    setError(null)
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current)
    else setAttempt((n) => n + 1) // chưa có widget (script chưa nạp được) → dựng lại từ đầu
  }, [])

  return { enabled: !!siteKey, token, pending: !!siteKey && !token, failed: !!error, error, reset, containerRef: setEl }
}

export function TurnstileBox({ ts, className }: { ts: TurnstileState; className?: string }) {
  const { t } = useTranslation('sharedMisc')
  if (!ts.enabled) return null
  return (
    <div className={className}>
      {/* 65px = chiều cao widget: giữ chỗ từ đầu để form không giật khi widget hiện. */}
      <div ref={ts.containerRef} className="min-h-[65px] w-full" data-testid="turnstile-box" />
      {ts.error && (
        <p className="mt-1.5 text-xs text-destructive" role="alert" data-testid="turnstile-error">
          {turnstileFailureMessage(ts.error)}{' '}
          <button type="button" className="font-medium underline" onClick={ts.reset}>{t('turnstile.retry')}</button>.
        </p>
      )}
    </div>
  )
}
